'use client';

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import Image from 'next/image';
import { Upload, Trash2, Eye, EyeOff, Plus, X, Loader2, AlertCircle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GalleryImage {
  id: string;
  name: string | null;
  description: string | null;
  image: string | null;
  isReleased: boolean;
}

interface Memorial {
  id: string;
  firstName: string | null;
  lastName: string | null;
  galleryImages: GalleryImage[];
}

interface PendingUpload {
  file: File;
  preview: string;
  name: string;
  uploading: boolean;
  error?: string;
}

export default function GalleryManagementPage() {
  const params = useParams();
  const slug = params.slug as string;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [memorial, setMemorial] = useState<Memorial | null>(null);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        // Use family API to get ALL images (including unreleased)
        const res = await fetch(`/api/family/${slug}`);
        if (!res.ok) throw new Error('Memorial not found');
        const data = await res.json();
        setMemorial(data);
        setImages(data.galleryImages || []);
      } catch (err) {
        console.error(err);
        setError('Gedenkseite nicht gefunden');
      } finally {
        setLoading(false);
      }
    };
    fetchMemorial();
  }, [slug]);

  const toggleVisibility = async (id: string) => {
    const image = images.find(img => img.id === id);
    if (!image) return;

    const newIsReleased = !image.isReleased;

    // Optimistic update
    setImages(prev =>
      prev.map(img =>
        img.id === id ? { ...img, isReleased: newIsReleased } : img
      )
    );

    try {
      const res = await fetch(`/api/admin/gallery/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isReleased: newIsReleased }),
      });

      if (!res.ok) throw new Error('Failed to update');
    } catch (err) {
      console.error(err);
      // Revert on error
      setImages(prev =>
        prev.map(img =>
          img.id === id ? { ...img, isReleased: !newIsReleased } : img
        )
      );
    }
  };

  const deleteImage = async (id: string) => {
    if (!confirm('Möchten Sie dieses Bild wirklich löschen?')) return;

    const imageToDelete = images.find(img => img.id === id);

    // Optimistic update
    setImages(prev => prev.filter(img => img.id !== id));

    try {
      const res = await fetch(`/api/admin/gallery/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Failed to delete');
    } catch (err) {
      console.error(err);
      // Revert on error
      if (imageToDelete) {
        setImages(prev => [...prev, imageToDelete]);
      }
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newUploads: PendingUpload[] = [];

    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 10 * 1024 * 1024) return; // 10MB limit

      const preview = URL.createObjectURL(file);
      newUploads.push({
        file,
        preview,
        name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
        uploading: false,
      });
    });

    setPendingUploads(prev => [...prev, ...newUploads]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removePendingUpload = (index: number) => {
    setPendingUploads(prev => {
      const newUploads = [...prev];
      URL.revokeObjectURL(newUploads[index].preview);
      newUploads.splice(index, 1);
      return newUploads;
    });
  };

  const uploadAllImages = async () => {
    if (pendingUploads.length === 0 || !memorial) return;

    setIsUploading(true);

    // Capture uploads to process (avoid stale state issues)
    const uploadsToProcess = [...pendingUploads];
    const successfulIds: number[] = [];
    const failedIds: number[] = [];

    for (let i = 0; i < uploadsToProcess.length; i++) {
      const upload = uploadsToProcess[i];

      // Mark as uploading
      setPendingUploads(prev =>
        prev.map((u, idx) => idx === i ? { ...u, uploading: true } : u)
      );

      try {
        // 1. Upload the file
        const formData = new FormData();
        formData.append('file', upload.file);
        formData.append('type', 'gallery');

        const uploadRes = await fetch('/api/admin/upload', {
          method: 'POST',
          body: formData,
        });

        if (!uploadRes.ok) throw new Error('Upload failed');
        const uploadData = await uploadRes.json();

        // 2. Create the gallery image record
        const createRes = await fetch(`/api/memorials/${slug}/gallery`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: upload.name,
            image: uploadData.url,
            isReleased: true,
          }),
        });

        if (!createRes.ok) throw new Error('Failed to save image');
        const newImage = await createRes.json();

        // Add to gallery
        setImages(prev => [newImage, ...prev]);
        successfulIds.push(i);

      } catch (err) {
        console.error(err);
        failedIds.push(i);
        setPendingUploads(prev =>
          prev.map((u, idx) => idx === i ? { ...u, uploading: false, error: 'Upload fehlgeschlagen' } : u)
        );
      }
    }

    // Remove successful uploads, keep failed ones
    setPendingUploads(prev => prev.filter((_, idx) => !successfulIds.includes(idx)));

    setIsUploading(false);

    // Close modal if all uploads succeeded
    if (failedIds.length === 0) {
      // Clean up previews for successful uploads
      successfulIds.forEach(idx => {
        if (uploadsToProcess[idx]) {
          URL.revokeObjectURL(uploadsToProcess[idx].preview);
        }
      });
      setPendingUploads([]);
      setShowUploadModal(false);
    }
  };

  const closeModal = () => {
    // Clean up previews
    pendingUploads.forEach(upload => URL.revokeObjectURL(upload.preview));
    setPendingUploads([]);
    setShowUploadModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    );
  }

  if (error || !memorial) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-serif text-gray-900">{error || 'Gedenkseite nicht gefunden'}</h1>
      </div>
    );
  }

  const visibleCount = images.filter(img => img.isReleased).length;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Bildergalerie</h1>
          <p className="text-gray-500 mt-1">
            {images.length} Bilder insgesamt, {visibleCount} öffentlich sichtbar
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Bilder hochladen
        </button>
      </div>

      {/* Gallery grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className={cn(
                'group relative bg-white rounded-xl border overflow-hidden',
                image.isReleased ? 'border-gray-200' : 'border-amber-300 bg-amber-50'
              )}
            >
              {/* Image */}
              <div className="relative aspect-square bg-gray-100">
                {image.image ? (
                  <Image
                    src={image.image}
                    alt={image.name || 'Galeriebild'}
                    fill
                    className={cn(
                      'object-cover transition-opacity',
                      !image.isReleased && 'opacity-50'
                    )}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    Kein Bild
                  </div>
                )}

                {/* Hidden badge */}
                {!image.isReleased && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded">
                    Versteckt
                  </div>
                )}
              </div>

              {/* Image info */}
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {image.name || 'Unbenannt'}
                </p>
                {image.description && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {image.description}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="absolute bottom-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => toggleVisibility(image.id)}
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    image.isReleased
                      ? 'bg-white/90 text-gray-600 hover:bg-gray-100'
                      : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                  )}
                  title={image.isReleased ? 'Verstecken' : 'Anzeigen'}
                >
                  {image.isReleased ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => deleteImage(image.id)}
                  className="p-2 bg-white/90 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  title="Löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Upload className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">Keine Bilder vorhanden</h2>
          <p className="text-gray-500 mb-6">Laden Sie Erinnerungsfotos hoch, um sie in der Galerie anzuzeigen.</p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Bilder hochladen
          </button>
        </div>
      )}

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-medium text-blue-900 mb-2">Tipps zur Bildergalerie</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Versteckte Bilder sind nur für Sie sichtbar, nicht für Besucher</li>
          <li>• Empfohlene Bildgröße: mindestens 800x600 Pixel</li>
          <li>• Unterstützte Formate: JPG, PNG, WebP</li>
        </ul>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-gray-900">Bilder hochladen</h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600"
                disabled={isUploading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                isDragging
                  ? 'border-memorial-primary bg-memorial-primary/5'
                  : 'border-gray-300 hover:border-memorial-primary'
              )}
            >
              <Upload className={cn(
                'w-12 h-12 mx-auto mb-4 transition-colors',
                isDragging ? 'text-memorial-primary' : 'text-gray-400'
              )} />
              <p className="text-gray-900 font-medium mb-1">
                Bilder hierher ziehen
              </p>
              <p className="text-sm text-gray-500 mb-4">
                oder klicken zum Auswählen
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
              <button
                type="button"
                className="px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
              >
                Dateien auswählen
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              Max. 10 MB pro Bild • JPG, PNG, WebP
            </p>

            {/* Pending uploads preview */}
            {pendingUploads.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="text-sm font-medium text-gray-900">
                  {pendingUploads.length} Bild{pendingUploads.length !== 1 ? 'er' : ''} ausgewählt
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {pendingUploads.map((upload, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={upload.preview}
                          alt={upload.name}
                          className="w-full h-full object-cover"
                        />
                        {upload.uploading && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                          </div>
                        )}
                        {upload.error && (
                          <div className="absolute inset-0 bg-red-500/50 flex items-center justify-center">
                            <AlertCircle className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      {!upload.uploading && !isUploading && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removePendingUpload(index);
                          }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeModal}
                disabled={isUploading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              {pendingUploads.length > 0 && (
                <button
                  onClick={uploadAllImages}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Lädt hoch...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Hochladen
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
